/* Connect a device — 4-step wizard: Brand → Method → Details → Set up on the device. */
import { useMemo, useState } from "react";
import type { BioProtocol, BioVendor, BioVendorInfo, Device, DeviceInput } from "../../../data/types";
import { ApiError } from "../../../data/provider";
import { useBioVendors, useConnectionInfo, useCreateDevice } from "../../../data/hooks";
import { Alert, Button, Chip, ErrorBox, Modal, SearchBox, SelectField, Skeleton, Steps, TextField, errMsg, useToast } from "../../../ui";
import { LOOKUP, useI18n } from "../../../i18n";
import { FileImport } from "./FileImport";
import { LiveCheck } from "./LiveCheck";
import { ValuesPanel } from "./ValuesPanel";
import { RECOMMENDED, TIMEZONES, protocolDesc, protocolLabel, protocolNeeds, serialHint } from "./labels";

type Step = 0 | 1 | 2 | 3;
interface Form { name: string; branch: string; serial: string; model: string; timezone: string; baseUrl: string; username: string; secret: string; pollIntervalSec: string }

const shortName = (v: BioVendorInfo) => v.name.split(/[ (]/)[0];

export function VendorCard({ v, on, onPick }: { v: BioVendorInfo; on: boolean; onPick: () => void }) {
  const { L } = useI18n();
  return (
    <button type="button" className={"vendor-card" + (on ? " on" : "")} onClick={onPick} aria-pressed={on} data-testid={"vendor-" + v.id}>
      <span className="row" style={{ gap: 10, flexWrap: "nowrap" }}>
        <span className="vendor-ini" aria-hidden="true">{v.name[0]}</span>
        <b style={{ minWidth: 0 }}>{v.name}</b>
      </span>
      <span className="models">{v.models.slice(0, 3).join(" · ")}</span>
      {v.realtime && <span><Chip tone="ok">{L("Real-time", "لحظي")}</Chip></span>}
    </button>
  );
}

export function ConnectDeviceWizard({ onClose, onDone }: { onClose: () => void; onDone: (d: Device) => void }) {
  const i = useI18n();
  const { L, t } = i;
  const toast = useToast();
  const vendors = useBioVendors();
  const info = useConnectionInfo();
  const create = useCreateDevice();
  const [step, setStep] = useState<Step>(0);
  const [q, setQ] = useState("");
  const [vendorId, setVendorId] = useState<BioVendor | null>(null);
  const [protocol, setProtocol] = useState<BioProtocol | null>(null);
  const [f, setF] = useState<Form>({ name: "", branch: Object.keys(LOOKUP.BR)[0] ?? "", serial: "", model: "", timezone: "Asia/Riyadh", baseUrl: "", username: "", secret: "", pollIntervalSec: "300" });
  const [errs, setErrs] = useState<Record<string, string>>({});
  const [formErr, setFormErr] = useState("");
  const [device, setDevice] = useState<Device | null>(null);
  const [testOk, setTestOk] = useState(false);

  const vendor = vendors.data?.find((v) => v.id === vendorId) ?? null;
  const list = useMemo(() => {
    const s = q.trim().toLowerCase();
    return (vendors.data ?? []).filter((v) => !s || v.name.toLowerCase().includes(s) || v.models.some((m) => m.toLowerCase().includes(s)));
  }, [vendors.data, q]);
  const set = <K extends keyof Form>(k: K, v: Form[K]) => { setF((x) => ({ ...x, [k]: v })); setErrs((e) => { const n = { ...e }; delete n[k]; return n; }); };

  const pickVendor = (v: BioVendorInfo) => {
    setVendorId(v.id);
    setProtocol(v.protocols.includes(RECOMMENDED[v.id]) ? RECOMMENDED[v.id] : v.protocols[0]);
    setF((x) => ({ ...x, name: x.name || shortName(v) + " · " + i.branch(x.branch), baseUrl: v.id === "anviz" && !x.baseUrl ? "https://api.crosschexcloud.com" : x.baseUrl }));
    setStep(1);
  };

  const submit = () => {
    if (!vendor || !protocol) return;
    const e: Record<string, string> = {};
    if (!f.name.trim()) e.name = L("Give the device a name", "أدخل اسمًا للجهاز");
    if (!f.serial.trim()) e.serial = L("Serial number is required", "الرقم التسلسلي مطلوب");
    else if (/[\s/?&]/.test(f.serial.trim())) e.serial = L("No spaces or / ? & characters", "بدون مسافات أو الرموز / ? &");
    if (protocol === "poll" && !f.baseUrl.trim()) e.baseUrl = L("Server URL is required for server sync", "عنوان الخادم مطلوب للمزامنة");
    else if (protocol === "poll" && !/^https?:\/\/\S+$/i.test(f.baseUrl.trim())) e.baseUrl = L("Must start with http:// or https://", "يجب أن يبدأ بـ http:// أو https://");
    setErrs(e); setFormErr("");
    if (Object.keys(e).length) return;
    const input: DeviceInput = { name: f.name.trim(), vendor: vendor.id, protocol, serial: f.serial.trim(), branch: f.branch, timezone: f.timezone, model: f.model || undefined };
    if (protocol === "poll") Object.assign(input, { baseUrl: f.baseUrl.trim(), username: f.username.trim(), secret: f.secret || undefined, pollIntervalSec: Number(f.pollIntervalSec) });
    create.mutate(input, {
      onSuccess: (d) => { setDevice(d); setStep(3); toast(L("Device added — now set it up", "تمت إضافة الجهاز — أكمل الإعداد")); },
      onError: (err) => {
        if (err instanceof ApiError && err.status === 409) { setErrs({ serial: L("This serial number is already registered", "هذا الرقم التسلسلي مسجل مسبقًا") }); return; }
        if (err instanceof ApiError && err.fields) { setErrs(err.fields); setFormErr(errMsg(err)); return; }
        setFormErr(errMsg(err));
      },
    });
  };

  const titles = [L("Brand", "العلامة"), L("Method", "طريقة الربط"), L("Details", "البيانات"), L("Set up on the device", "الإعداد في الجهاز")];
  const branchOpts: [string, string][] = Object.keys(LOOKUP.BR).map((k) => [k, i.branch(k)]);
  const userLabel = vendor?.id === "anviz" ? L("API key", "مفتاح API") : L("Username", "اسم المستخدم");
  const secretLabel = vendor?.id === "anviz" ? L("API secret", "سرّ API") : L("Password / API key", "كلمة المرور / مفتاح API");

  let body: React.ReactNode;
  if (vendors.error) body = <ErrorBox error={vendors.error} onRetry={() => vendors.refetch()} />;
  else if (!vendors.data) body = <Skeleton />;
  else if (step === 0) {
    body = (
      <div className="stack" data-testid="wiz-brand">
        <p className="sm muted" style={{ margin: 0 }}>{L("Pick the brand printed on your attendance device or software.", "اختر العلامة التجارية المطبوعة على جهاز الحضور أو برنامجه.")}</p>
        <SearchBox value={q} onChange={setQ} placeholder={L("Search brand or model…", "ابحث عن علامة أو طراز…")} testId="vendor-search" />
        <div className="vendor-grid">{list.map((v) => <VendorCard key={v.id} v={v} on={v.id === vendorId} onPick={() => pickVendor(v)} />)}</div>
        {!list.length && <p className="sm muted" data-testid="vendor-none">{L("No brand matches — use “Any other device”.", "لا توجد علامة مطابقة — استخدم «أي جهاز آخر».")}</p>}
        <div className="sm">{L("Not listed?", "غير موجود؟")} <button type="button" className="link" style={{ background: "none", border: 0, color: "var(--primary)", cursor: "pointer", font: "inherit", padding: 0 }}
          onClick={() => { const g = vendors.data?.find((v) => v.id === "generic"); if (g) pickVendor(g); }} data-testid="vendor-other">{L("→ Any other device", "← أي جهاز آخر")}</button></div>
      </div>
    );
  } else if (step === 1 && vendor) {
    body = (
      <div className="stack" data-testid="wiz-method">
        <p className="sm muted" style={{ margin: 0 }}>{L(`How should ${shortName(vendor)} send attendance to StartHR?`, `كيف يرسل ${shortName(vendor)} الحضور إلى ستارت HR؟`)}</p>
        <div className="method-list" role="radiogroup" aria-label={L("Connection method", "طريقة الربط")}>
          {vendor.protocols.map((p) => (
            <button key={p} type="button" role="radio" aria-checked={p === protocol} className={"radio-card" + (p === protocol ? " on" : "")} onClick={() => setProtocol(p)} data-testid={"method-" + p}>
              <span className="rc-dot" aria-hidden="true" />
              <span className="rc-b">
                <span className="row" style={{ gap: 8 }}><b>{protocolLabel(p, L)}</b>{p === RECOMMENDED[vendor.id] && <Chip tone="ok" testId="recommended">{L("Recommended", "موصى به")}</Chip>}</span>
                <span className="sm">{protocolDesc(p, L)}</span>
                <span className="rc-need">{L("Needs: ", "يتطلب: ")}{protocolNeeds(p, vendor.id, L)}</span>
              </span>
            </button>
          ))}
        </div>
      </div>
    );
  } else if (step === 2 && vendor && protocol) {
    body = (
      <form className="stack" data-testid="wiz-details" onSubmit={(e) => { e.preventDefault(); submit(); }} noValidate>
        {formErr && <Alert tone="bad" title={formErr} testId="wiz-error" />}
        <div className="form2">
          <TextField label={L("Device name", "اسم الجهاز")} req value={f.name} onChange={(v) => set("name", v)} err={errs.name} testId="f-name" />
          <SelectField label={t("Branch")} value={f.branch} onChange={(v) => set("branch", v)} options={branchOpts} testId="f-branch" />
        </div>
        <TextField label={protocol === "agent" ? L("Agent / PC name", "اسم الوكيل / الحاسب") : L("Serial number", "الرقم التسلسلي")} req value={f.serial} onChange={(v) => set("serial", v)} err={errs.serial} hint={serialHint(vendor.id, protocol, L)} dir="ltr" testId="f-serial" />
        <div className="form2">
          <SelectField label={L("Model", "الطراز")} value={f.model} onChange={(v) => set("model", v)} options={[["", L("Not sure", "غير متأكد")], ...vendor.models.map((m): [string, string] => [m, m])]} testId="f-model" />
          <SelectField label={L("Time zone", "المنطقة الزمنية")} value={f.timezone} onChange={(v) => set("timezone", v)} options={TIMEZONES} err={errs.timezone} testId="f-timezone" />
        </div>
        {protocol === "poll" && (
          <>
            <TextField label={L("Server URL", "عنوان الخادم")} req value={f.baseUrl} onChange={(v) => set("baseUrl", v)} err={errs.baseUrl} placeholder="https://biotime.example.com" dir="ltr" testId="f-baseurl"
              hint={L("Must be reachable from the internet. Office-only addresses (192.168.x.x) need the StartHR Agent.", "يجب أن يكون متاحًا عبر الإنترنت. العناوين المحلية (192.168.x.x) تتطلب وكيل ستارت HR.")} />
            <div className="form2">
              <TextField label={userLabel} value={f.username} onChange={(v) => set("username", v)} dir="ltr" testId="f-username" />
              <TextField label={secretLabel} type="password" value={f.secret} onChange={(v) => set("secret", v)} dir="ltr" testId="f-secret" hint={L("Stored encrypted — never shown again", "يُحفظ مشفّرًا — ولن يُعرض مرة أخرى")} />
            </div>
            <SelectField label={L("Fetch every", "الجلب كل")} value={f.pollIntervalSec} onChange={(v) => set("pollIntervalSec", v)} err={errs.pollIntervalSec} testId="f-interval"
              options={[["60", L("1 minute", "دقيقة")], ["300", L("5 minutes", "5 دقائق")], ["900", L("15 minutes", "15 دقيقة")], ["3600", L("1 hour", "ساعة")]]} />
          </>
        )}
        <button type="submit" hidden aria-hidden="true" tabIndex={-1} />
      </form>
    );
  } else if (step === 3 && vendor && device) {
    body = (
      <div className="stack" data-testid="wiz-setup">
        {device.protocol !== "file" && (
          <div>
            <h4 style={{ margin: "0 0 8px" }}>{L("On the device", "في الجهاز")}</h4>
            <Steps steps={(i.isAr ? vendor.setupAr : vendor.setup)} />
          </div>
        )}
        {info.data ? <ValuesPanel device={device} info={info.data} token={device.webhookToken} onTestResult={setTestOk} /> : info.error ? <ErrorBox error={info.error} /> : <Skeleton />}
        {device.protocol === "file" ? <FileImport deviceId={device.id} /> : <LiveCheck device={device} token={device.webhookToken} testOk={testOk} onSkip={() => onDone(device)} />}
      </div>
    );
  }

  const foot = (
    <div className="wiz-foot">
      <div>{step > 0 && step < 3 && <Button variant="ghost" onClick={() => setStep((s) => (s - 1) as Step)} data-testid="wiz-back">{t("Back")}</Button>}</div>
      <div className="row">
        {step < 3 && <Button onClick={onClose}>{t("Cancel")}</Button>}
        {step === 0 && <Button variant="pri" disabled={!vendorId} onClick={() => setStep(1)} data-testid="wiz-next">{t("Next")}</Button>}
        {step === 1 && <Button variant="pri" disabled={!protocol} onClick={() => setStep(2)} data-testid="wiz-next">{t("Next")}</Button>}
        {step === 2 && <Button variant="pri" onClick={submit} loading={create.isPending} data-testid="wiz-create">{L("Add device", "إضافة الجهاز")}</Button>}
        {step === 3 && device && <Button variant="pri" onClick={() => onDone(device)} data-testid="wiz-done">{t("Done")}</Button>}
      </div>
    </div>
  );

  return (
    <Modal title={L("Connect a device", "ربط جهاز")} onClose={onClose} foot={foot} className="wide" testId="connect-wizard">
      <div className="stepper" aria-hidden="true">{titles.map((_, k) => <span key={k} className={k <= step ? "on" : ""} />)}</div>
      <div className="xs muted" style={{ marginTop: -8, marginBottom: 12 }} data-testid="wiz-step">{L(`Step ${step + 1} of 4`, `الخطوة ${step + 1} من 4`)} · <b>{titles[step]}</b></div>
      {body}
    </Modal>
  );
}
